import { useEffect, useRef } from "react";
import { motion, useInView, animate, useMotionValue, useTransform } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

export function Reveal({ children, delay = 0, y = 28, className = "", once = true }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.15 }}
      transition={{ duration: 0.8, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function MaskLines({ lines, className = "", lineClassName = "", delay = 0, stagger = 0.14 }) {
  return (
    <div className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
          <motion.span
            className={`block will-change-transform ${lineClassName}`}
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.95, delay: delay + i * stagger, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </div>
  );
}

export function Eyebrow({ children, className = "" }) {
  return (
    <p className={`font-hud text-[11px] md:text-xs uppercase tracking-[0.28em] text-kinetic ${className}`}>
      {children}
    </p>
  );
}

export function SectionHead({ sec, eyebrow, title, sub, className = "" }) {
  return (
    <div className={className}>
      <Reveal>
        <div className="flex items-center gap-3">
          <span className="font-hud text-[11px] tracking-[0.28em] text-kinetic">[{sec}]</span>
          <span className="h-px w-10 bg-kinetic/40" />
          <Eyebrow className="text-[#9CA3AF]">{eyebrow}</Eyebrow>
        </div>
      </Reveal>
      <Reveal delay={0.08}>
        <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
          {title}
        </h2>
      </Reveal>
      {sub ? (
        <Reveal delay={0.16}>
          <p className="mt-5 max-w-2xl text-[#9CA3AF] text-base md:text-lg leading-relaxed">{sub}</p>
        </Reveal>
      ) : null}
    </div>
  );
}

export function Counter({ value, suffix = "", prefix = "", className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const mv = useMotionValue(0);
  const text = useTransform(mv, (v) =>
    `${prefix}${Math.round(v).toLocaleString("en-IN")}${suffix}`
  );

  useEffect(() => {
    if (inView) {
      const controls = animate(mv, value, { duration: 1.8, ease: EASE });
      return () => controls.stop();
    }
  }, [inView, value, mv]);

  return (
    <span ref={ref} className={className}>
      <motion.span>{text}</motion.span>
    </span>
  );
}

export function HUDFrame({ children, className = "", testid }) {
  const corner = "absolute h-4 w-4 border-kinetic/70 z-10 pointer-events-none";
  return (
    <div className={`relative ${className}`} data-testid={testid}>
      <span className={`${corner} left-0 top-0 border-l-2 border-t-2`} />
      <span className={`${corner} right-0 top-0 border-r-2 border-t-2`} />
      <span className={`${corner} left-0 bottom-0 border-l-2 border-b-2`} />
      <span className={`${corner} right-0 bottom-0 border-r-2 border-b-2`} />
      {children}
    </div>
  );
}
